import {
  DataGridLayout,
  DateTimeRangeFilter,
  Filters,
  FiltersLayout,
  List,
  ListHeader,
  ListPageLayout,
  SearchInput,
} from 'framework/list';
import { useAuthorization } from 'framework/authorization';
import PublishVacancyButton from '../shared/button/PublishVacancyButton';
import { SYCOM_VACANCY_PUBLICATION_PLATFORM } from 'domain/recruiting/vacancy/publication/shared/vacancyPublicationService';
import VacancyPublishedOnBadge from 'domain/recruiting/vacancy/shared/badge/VacancyPublishedOnBadge';
import CopyVacancyButton from '../shared/button/CopyVacancyButton';
import { PeopleManagerFilter } from 'domain/shared/employee';
import EditVacancyButton from 'domain/recruiting/vacancy/shared/button/EditVacancyButton';
import { CreateButton } from 'framework/ui/ui-builder';
import { BadgeField, EnumField, SearchLinkField, SearchResultField } from 'framework/ui/field';
import VacancyStatusField from 'domain/recruiting/vacancy/shared/field/VacancyStatusField';
import VacancyStatusFilter from 'domain/recruiting/vacancy/List/filter/VacancyStatusFilter';
import useVacancyService, {
  CREATE_VACANCY,
  DISPLAY_UPDATE_BUTTON,
  VACANCY_DUE_DATE,
} from 'domain/recruiting/vacancy/shared/vacancyService';
import { useVacancyUrl } from 'domain/recruiting/vacancy/vacancyRoute';

const VacanciesList = () => {
  const { search } = useVacancyService();
  const { getCreateUrl, getSingleEntityUrl } = useVacancyUrl();
  const { isGranted } = useAuthorization();

  const filters = (
    <Filters>
      <FiltersLayout>
        <VacancyStatusFilter label="Status" source="status" />
        <PeopleManagerFilter source="requestedBy.alias" label="Requested by" />
        <DateTimeRangeFilter label="Created at" source="createdAt" />
      </FiltersLayout>
    </Filters>
  );

  return (
    <List getDataMethod={search} defaultSortOrder="DESC" defaultSortField="updatedAt">
      <ListPageLayout
        header={<ListHeader label="Vacancies" icon="fa-ship" />}
        actions={[
          <CreateButton
            url={getCreateUrl()}
            label="Create Vacancy"
            key="create-vacancy"
            permissionToCheck={CREATE_VACANCY}
          />,
        ]}
        filter={filters}
        search={<SearchInput label="vacancy name or ID" />}
      >
        <DataGridLayout
          buttons={[
            <EditVacancyButton
              key="edit-vacancy"
              source="alias"
              outline
              isVisible={() => isGranted(DISPLAY_UPDATE_BUTTON)}
              permissionToCheck=""
              sx={{ m: 0 }}
            />,
            <CopyVacancyButton
              key="copy-vacancy"
              source="alias"
              permissionToCheck={CREATE_VACANCY}
              sx={{ m: 0 }}
              variant="outlined"
            />,
            <PublishVacancyButton
              key="publish-vacancy"
              source="alias"
              outline
              sx={{ m: 0 }}
              isVisible={vacancy => vacancy.status === 'OPEN'}
              permissionToCheck={DISPLAY_UPDATE_BUTTON}
            />,
          ]}
        >
          <SearchLinkField
            source="alias"
            url={(id?: Nullable<StringOrNumber>) => getSingleEntityUrl(id)}
            value=""
            label="Vacancy ID"
          />
          <SearchResultField source="name" isSortable label="Position" width={200} />
          <BadgeField
            badges={[<VacancyStatusField source="status" label="Status" isSortable width={100} />]}
            label="Status"
          />
          <EnumField source="dueDate" label="Due date" isSortable options={VACANCY_DUE_DATE} />
          <BadgeField
            badges={[
              <VacancyPublishedOnBadge
                source="publications"
                platform={SYCOM_VACANCY_PUBLICATION_PLATFORM}
                text="SYCOM"
                isVisible={data =>
                  data.publications &&
                  data.publications.find((p: { type: string }) => p.type === SYCOM_VACANCY_PUBLICATION_PLATFORM)
                }
              />,
            ]}
            label="Published on"
          />
        </DataGridLayout>
      </ListPageLayout>
    </List>
  );
};

export default VacanciesList;
